import React, { FunctionComponent } from 'react'
import { useGetSelectedTheme, useIsDarkMode } from '@afterpaytouch/core'
import Image from 'next/image'
import { ILLUSTRATION_CONFIG, THEME_COLOR_MAP } from './constants'

import styles from './style.module.css'
import { IllustrationProps } from './types'

/**
 * The Illustration component renders themed and color-appropriate illustrations based on the current theme and dark mode settings.
 * It will use an optional passed in height, but if not provided, it calculates the height of the image based on the provided width and the aspect ratio defined in the illustration configuration.
 * The component uses Next.js's Image component for optimized image rendering.
 *
 * Currently illustrations are based on Arcade illustrations found here: https://apps--arcade-s3.stage.sqprod.co/apps/cabinet/illustrations/
 * Names are based on the names given to the illustrations in Arcade - ie eye-recognition (or Illustrations.EyeRecognition) matches https://apps--arcade-s3.stage.sqprod.co/apps/cabinet/illustrations/?illustration=Illustration-eye-recognition
 */
export const Illustration: FunctionComponent<IllustrationProps> = ({ name, alt, width, height, testId }) => {
  const isDarkMode = useIsDarkMode()
  const theme = useGetSelectedTheme()

  const colorScheme = isDarkMode ? 'dark' : 'light'
  const illustrationConfig = ILLUSTRATION_CONFIG[name]
  const illustration = illustrationConfig.variants[THEME_COLOR_MAP[theme]][colorScheme]

  const calculatedHeight = Number(width) / illustrationConfig.aspectRatio
  const roundedCalculatedHeight = Math.round(calculatedHeight)

  return <Image className={styles.image} src={illustration} alt={alt || ''} data-testid={testId} width={width} height={height || roundedCalculatedHeight} />
}
